import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Video, ExternalLink, Save, RotateCcw, Play } from "lucide-react";

// Mirror of REFERENCE_VIDEOS in /app/backend/protocol_warmup.py.
const VIDEO_KEYS = [
  { key: "active_recovery",  label: "Active Recovery — concept (Phase 1)",               default_url: null },
  { key: "lax_ball",         label: "Lax Ball — upper-half soft tissue (Phase 1)",       default_url: "https://www.youtube.com/shorts/8sYrs8QHi4Y" },
  { key: "j_bands",          label: "J-Band Circuit (Warmup + Phase 2)",                 default_url: "https://www.youtube.com/watch?v=DadrSfSb9fw" },
  { key: "j_bands_reverse",  label: "J-Bands reverse circuit (Phase 2 · starts 9:08)",   default_url: "https://www.youtube.com/watch?v=DadrSfSb9fw&t=548s" },
  { key: "drop_catch",       label: "Drop Catch eccentrics (Phase 2)",                   default_url: "https://youtu.be/Wiblq0PJICk" },
  { key: "prone_t_hovers",   label: "Prone T Hovers (Phase 2)",                          default_url: "https://www.youtube.com/shorts/oDvH2QKiyas" },
  { key: "walking_ts",       label: "Walking T’s with Arm Circles — 5 ways (Phase 2)",   default_url: "https://www.youtube.com/watch?v=85LD7jKtbaw" },
  { key: "arm_care_drills",  label: "7 Baseball Arm Care & Recovery Drills (Phase 2)",   default_url: "https://www.youtube.com/watch?v=JItoPvJYdTA" },
  { key: "hip_flow",         label: "Hip Flow Circuit (Phase 4)",                        default_url: "https://youtu.be/yE2XJZHXc6s?t=6" },
  { key: "sldl",             label: "SLDL — posterior chain (Phase 4)",                  default_url: "https://youtu.be/oocDqnz-cTk" },
  { key: "med_ball",         label: "Med-Ball reset (Phase 4)",                          default_url: "https://youtu.be/PnD1q4Nj9l4" },
  { key: "mobility_flow",    label: "Mobility Flow — 5-move flow (Phase 4)",             default_url: "https://youtu.be/wCUI1bwlJqA" },
];

/**
 * Pull the YouTube video ID from any of:
 *   youtu.be/<id>           ·  youtube.com/watch?v=<id>
 *   youtube.com/shorts/<id> ·  youtube.com/embed/<id>
 * Returns null if not recognizable.
 */
function youtubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1) || null;
    const m = u.pathname.match(/\/(shorts|embed)\/([\w-]{6,})/);
    if (m) return m[2];
    if (u.pathname === "/watch") return u.searchParams.get("v");
    return null;
  } catch { return null; }
}

function vimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

function Thumbnail({ url }) {
  const yid = youtubeId(url);
  const vid = vimeoId(url);
  const [src, setSrc] = useState(yid ? `https://img.youtube.com/vi/${yid}/mqdefault.jpg` : null);

  if (!url) {
    return (
      <div className="w-24 h-14 rounded-md flex items-center justify-center shrink-0 text-white/30 text-[10px] font-bold" style={{ backgroundColor: "var(--ink-2)" }}>
        NO VIDEO
      </div>
    );
  }
  if (yid && src) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="relative w-24 h-14 rounded-md overflow-hidden shrink-0 group block ring-1 ring-white/10 hover:ring-white/30 transition" title="Open video in new tab" data-testid="video-thumbnail">
        <img src={src} onError={() => setSrc(null)} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition flex items-center justify-center">
          <Play className="h-5 w-5 text-white drop-shadow" fill="white" />
        </div>
      </a>
    );
  }
  // Vimeo or other — show a generic play tile linking out
  return (
    <a href={url} target="_blank" rel="noreferrer" className="w-24 h-14 rounded-md overflow-hidden shrink-0 flex items-center justify-center ring-1 ring-white/10 hover:ring-white/30" style={{ backgroundColor: vid ? "#0d2436" : "var(--ink-2)" }} title="Open video in new tab" data-testid="video-thumbnail-generic">
      <Play className="h-5 w-5 text-white/80" fill="currentColor" />
    </a>
  );
}

export default function RecoveryVideoOverridesCard() {
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/teams/me/recovery-video-overrides"); setOverrides(data.overrides || {}); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const setVal = (k, v) => setOverrides((o) => ({ ...o, [k]: v }));
  const clear = (k) => setOverrides((o) => { const n = { ...o }; delete n[k]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(overrides).filter(([, v]) => v && v.trim()));
      await api.put("/teams/me/recovery-video-overrides", { overrides: clean });
      toast.success("Recovery videos updated for your team");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <section className="rounded-xl ink-card p-6" data-testid="recovery-video-overrides-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-lg text-white flex items-center gap-2">
            <Video className="h-5 w-5" style={{ color: "var(--brand-secondary)" }} />
            Recovery Videos
          </h2>
          <p className="text-sm text-white/50 mt-1">Replace any of the platform&apos;s reference clips with your own team videos. Paste a YouTube or Vimeo URL; leave blank to keep the default. Thumbnails preview the active clip — click to verify before saving.</p>
        </div>
        <Button onClick={save} disabled={saving} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="recovery-videos-save">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save videos"}
        </Button>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        {VIDEO_KEYS.map((v) => {
          const overrideUrl = overrides[v.key];
          const effective = (overrideUrl && overrideUrl.trim()) || v.default_url;
          const isOverridden = !!(overrideUrl && overrideUrl.trim());
          return (
            <div key={v.key} className="rounded-lg border border-[var(--ink-border)] p-4 flex gap-3" style={{ backgroundColor: "var(--ink-1)" }}>
              <Thumbnail url={effective} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-white/85 text-xs font-semibold">{v.label}</Label>
                  {isOverridden && (
                    <span className="text-[9px] tracking-[0.2em] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(245,158,11,0.15)", color: "#FCD34D" }}>Override</span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={overrideUrl || ""}
                    onChange={(e) => setVal(v.key, e.target.value)}
                    placeholder={v.default_url ? "Override URL (leave blank for default)" : "Paste a YouTube/Vimeo URL"}
                    className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white text-sm flex-1"
                    data-testid={`recovery-video-input-${v.key}`}
                  />
                  {isOverridden && (
                    <Button variant="ghost" size="icon" onClick={() => clear(v.key)} className="text-white/40 hover:text-red-400 shrink-0" title="Clear override — revert to default" data-testid={`recovery-video-clear-${v.key}`}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                  {effective && (
                    <a href={effective} target="_blank" rel="noreferrer" className="text-xs text-white/40 hover:text-white inline-flex items-center gap-1 shrink-0" title="Open current video" data-testid={`recovery-video-open-${v.key}`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                {!isOverridden && v.default_url && (
                  <p className="text-[10px] text-white/35 mt-1.5 truncate">Default: {v.default_url}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
